import { GlobalAlert } from '@thoughtspot/global-alert';
import { useTranslation } from '@thoughtspot/i18n';
import { Alert, ErrorCode } from '@thoughtspot/radiant-react/widgets/alert';
import { ContentGrid, Vertical, } from '@thoughtspot/radiant-react/widgets/layout';
import { SearchBar } from '@thoughtspot/radiant-react/widgets/search-bar';
import React, { useCallback, useState } from 'react';
import { LoaderIndicator } from '../../../loader';
import { ConfigCard, InputType } from '../../config-components';
import { FeatureManagementTranslationKeys } from '../../constants';
import { DisableFeatureModal } from '../disable-feature-modal';
import styles from './org-feature-management.module.scss';
import { useOrgFeatures } from './use-org-features';
const DATA_MODELLING_CATEGORY = 'dataModelling';
const DATA_MODELLING_FEATURE_TITLE_KEY = 'isTmlUtilityV2Enabled.title';
const noop = () => { };
export const OrgFeatureManagement = ({ onSearch = noop, onDisableModalCancel = noop, }) => {
    const { t } = useTranslation();
    const [searchTerm, setSearchTerm] = useState('');
    const [isToastVisible, setIsToastVisible] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [disableModalState, setDisableModalState] = useState({ isOpen: false, feature: null });
    const { features, initialLoading, contentLoading, updateFeatureValue, } = useOrgFeatures({ searchTerm });
    const showToast = useCallback((message) => {
        setToastMessage(message);
        setIsToastVisible(true);
    }, []);
    const handleValueChange = useCallback((configName, newValue, feature) => {
        if (feature.value === 'true' && newValue === 'false') {
            setDisableModalState({ isOpen: true, feature });
            return;
        }
        updateFeatureValue(configName, newValue);
        const label = t(`${configName}.label`) || configName;
        showToast(t(FeatureManagementTranslationKeys.TOAST_ENABLED, {
            featureName: label,
        }));
    }, [updateFeatureValue, showToast, t]);
    const handleDisableConfirm = useCallback(() => {
        const { feature } = disableModalState;
        if (!feature)
            return;
        updateFeatureValue(feature.configName, 'false');
        const label = t(`${feature.configName}.label`) || feature.configName;
        showToast(t(FeatureManagementTranslationKeys.TOAST_DISABLED, {
            featureName: label,
        }));
        setDisableModalState({ isOpen: false, feature: null });
    }, [disableModalState, updateFeatureValue, showToast, t]);
    const handleDisableDismiss = useCallback(() => {
        const { feature } = disableModalState;
        if (feature) {
            const label = t(`${feature.configName}.label`) || feature.configName;
            onDisableModalCancel(feature.configName, label);
        }
        setDisableModalState({ isOpen: false, feature: null });
    }, [disableModalState, onDisableModalCancel, t]);
    const handleSearchChange = useCallback((term) => {
        setSearchTerm(term);
        if (term) {
            onSearch(term);
        }
    }, [onSearch]);
    if (initialLoading) {
        return (React.createElement("div", { className: styles.loadingContainer },
            React.createElement(LoaderIndicator, null)));
    }
    const renderContent = () => {
        if (contentLoading) {
            return (React.createElement("div", { className: styles.contentLoadingContainer },
                React.createElement(LoaderIndicator, null)));
        }
        if (features.length === 0) {
            return (React.createElement(Alert.Muted, { global: true, imageVisible: true, errorCode: searchTerm
                    ? ErrorCode.GLOBAL_SEARCH
                    : ErrorCode.GLOBAL_GENERAL_WARNING, title: searchTerm
                    ? t(FeatureManagementTranslationKeys.NO_SEARCH_RESULTS_TITLE)
                    : t(FeatureManagementTranslationKeys.NO_FEATURES_TITLE), description: searchTerm
                    ? t(FeatureManagementTranslationKeys.NO_SEARCH_RESULTS_DESCRIPTION)
                    : t(FeatureManagementTranslationKeys.NO_FEATURES_DESCRIPTION), "data-testid": "org-features-no-data", className: styles.emptyState }));
        }
        return (React.createElement("div", { className: styles.cardListContainer }, features.map(feature => {
            const label = feature.category === DATA_MODELLING_CATEGORY
                ? t(DATA_MODELLING_FEATURE_TITLE_KEY)
                : t(`${feature.configName}.label`) ||
                    feature.configName;
            const infoTooltipKey = `${feature.configName}.infoTooltip`;
            const infoTooltipValue = t(infoTooltipKey);
            const description = infoTooltipValue !== infoTooltipKey
                ? infoTooltipValue
                : undefined;
            return (React.createElement(ConfigCard, { key: feature.configName, inputType: InputType.Toggle, title: label, description: description, checked: feature.value === 'true', onChange: e => {
                    const target = e.target;
                    const newValue = target.checked
                        ? 'true'
                        : 'false';
                    handleValueChange(feature.configName, newValue, feature);
                }, name: feature.configName, "data-testid": `feature-card-${feature.configName}` }));
        })));
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(GlobalAlert.Toast, { isVisible: isToastVisible, message: toastMessage, onClose: () => setIsToastVisible(false), autoHide: true }),
        React.createElement(DisableFeatureModal, { featureName: disableModalState.feature
                ? t(`${disableModalState.feature.configName}.label`) || disableModalState.feature.configName
                : '', isOpen: disableModalState.isOpen, onConfirm: handleDisableConfirm, onDismiss: handleDisableDismiss }),
        React.createElement(Vertical, { grow: true, className: styles.outerWrapper, role: "region", "aria-label": t(FeatureManagementTranslationKeys.TITLE) },
            React.createElement(ContentGrid, { span: 10, grow: true },
                React.createElement("div", { className: styles.contentContainer },
                    React.createElement("div", { className: styles.searchBarWrapper },
                        React.createElement(SearchBar, { searchValue: searchTerm, searchPlaceholder: t(FeatureManagementTranslationKeys.SEARCH_PLACEHOLDER), onSearchChange: handleSearchChange, ariaLabelPrefix: t(FeatureManagementTranslationKeys.TITLE) })),
                    renderContent())))));
};
//# sourceMappingURL=org-feature-management.js.map